// Implements: F-UIX-019
/**
 * One row of the platform totals: its label, its figures, and a disclosure with the
 * tokens split in and out and the money per currency. It holds no state: whether the
 * detail is open belongs to the row's container, keyed by organization id.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { PLATFORM_COUNT_GROUPING, PLATFORM_LOCALE } from "../../../types/locale";
import { costText } from "../model/money";
import type { RowCost, RowFigures } from "../model/platformRows";
import styles from "./PlatformTotals.module.css";

const COUNT = new Intl.NumberFormat(PLATFORM_LOCALE, { maximumFractionDigits: 0, useGrouping: PLATFORM_COUNT_GROUPING });

/** A whole number in the platform locale ("2150", "21,500"). */
export function countText(value: number): string {
  return COUNT.format(value);
}

/** Every amount of a row, one per currency, or `none` when it cost nothing. */
export function costsText(costs: readonly RowCost[], none: string): string {
  return costs.length === 0 ? none : costs.map((cost) => costText(cost.micros, cost.currency)).join(" · ");
}

/** The five figure cells every row shares: outcomes, tokens and money. */
export function FigureCells({ figures }: { readonly figures: RowFigures }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <td>{countText(figures.delivered)}</td>
      <td>{countText(figures.failed)}</td>
      <td>{countText(figures.cancelled)}</td>
      <td>{countText(figures.promptTokens + figures.completionTokens)}</td>
      <td>{costsText(figures.costs, t("admin.totals.noCost"))}</td>
    </>
  );
}

export interface TotalsRowProps {
  readonly label: string;
  readonly figures: RowFigures;
  readonly expanded: boolean;
  /** The id of the detail row, for `aria-controls`. */
  readonly detailId: string;
  readonly onToggle: () => void;
}

export function TotalsRow({ label, figures, expanded, detailId, onToggle }: TotalsRowProps): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <tr>
        <th scope="row">{label}</th>
        <FigureCells figures={figures} />
        <td>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={onToggle}
          >
            {expanded ? t("admin.totals.hideDetail") : t("admin.totals.showDetail")}
          </button>
        </td>
      </tr>
      {/* Always in the tree, hidden when closed, so `aria-controls` always names an element. */}
      <tr id={detailId} hidden={!expanded}>
          <td colSpan={7}>
            <dl className={styles.detail}>
              <dt>{t("admin.totals.promptTokens")}</dt>
              <dd>{countText(figures.promptTokens)}</dd>
              <dt>{t("admin.totals.completionTokens")}</dt>
              <dd>{countText(figures.completionTokens)}</dd>
              <dt>{t("admin.totals.costs")}</dt>
              <dd>{costsText(figures.costs, t("admin.totals.noCost"))}</dd>
            </dl>
          </td>
      </tr>
    </>
  );
}
